import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Loader2, CheckCircle2 } from "lucide-react";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    business_name: user?.business_name || "",
    phone: user?.phone || "",
    state: user?.state || "",
    rating_badge_enabled: user?.rating_badge_enabled !== false,
  });
  const [payout, setPayout] = useState({
    account_name: user?.payout_details?.account_name || "",
    bsb: user?.payout_details?.bsb || "",
    account_number: user?.payout_details?.account_number || "",
    bank_name: user?.payout_details?.bank_name || "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setError("");
    const payload = {
      name: form.name.trim() || undefined,
      business_name: form.business_name.trim() || null,
      phone: form.phone.trim() || null,
      state: form.state || null,
      rating_badge_enabled: form.rating_badge_enabled,
    };
    const res = await updateProfile(payload);
    setSaving(false);
    if (res.ok) {
      setMsg("Profile updated.");
    } else {
      setError(res.error);
    }
  };

  return (
    <section className="p-6 sm:p-10" data-testid="profile-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-10">
          <div className="gt-label mb-2">Account</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
            Organiser profile
          </h2>
          <p className="text-[var(--gt-ink-2)] text-sm mt-2">
            Keep your details current. Payouts will use the bank account below once ticket sales go live.
          </p>
        </div>

        <form onSubmit={onSave} className="space-y-10" data-testid="profile-form">
          {/* Organiser details */}
          <div className="gt-card">
            <div className="gt-label mb-4">Organiser details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="gt-form-label">Email</label>
                <input
                  className="gt-input bg-[var(--gt-bg-surface)] cursor-not-allowed"
                  value={user?.email || ""}
                  readOnly
                  data-testid="profile-email-readonly"
                />
              </div>
              <div>
                <label className="gt-form-label">Name</label>
                <input
                  className="gt-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <label className="gt-form-label">Business or brand</label>
                <input
                  className="gt-input"
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  data-testid="profile-business-input"
                />
              </div>
              <div>
                <label className="gt-form-label">Phone</label>
                <input
                  className="gt-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+61 4XX XXX XXX"
                  data-testid="profile-phone-input"
                />
              </div>
              <div>
                <label className="gt-form-label">State</label>
                <select
                  className="gt-input"
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  data-testid="profile-state-select"
                >
                  <option value="">Select a state</option>
                  {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-6 border-t border-[var(--gt-line)] pt-6">
              <label className="gt-form-label">Show public rating badge</label>
              <p className="text-xs text-[var(--gt-ink-2)] mb-3">
                Display your average attendee rating (e.g. ★ 4.6) on your public organiser page once you have at least 5 verified ratings.
              </p>
              <div className="flex gap-2 max-w-xs">
                {[true, false].map((v) => (
                  <button
                    key={String(v)}
                    type="button"
                    onClick={() => setForm({ ...form, rating_badge_enabled: v })}
                    className={`flex-1 border rounded-sm px-4 py-3 text-sm font-semibold transition-colors ${
                      form.rating_badge_enabled === v
                        ? "border-[var(--gt-ink)] bg-[var(--gt-ink)] text-white"
                        : "border-[var(--gt-line)] bg-white text-[var(--gt-ink-2)] hover:border-[var(--gt-ink-2)]"
                    }`}
                    data-testid={v ? "rating-badge-toggle-on" : "rating-badge-toggle-off"}
                  >
                    {v ? "On" : "Off"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Payouts placeholder */}
          <div className="gt-card">
            <div className="flex items-center justify-between mb-4">
              <div className="gt-label">Bank account · Payouts (AUD)</div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--gt-ink-3)]">
                Locked until first sale
              </span>
            </div>
            <p className="text-[var(--gt-ink-2)] text-sm mb-6">
              We settle in AUD to your Australian bank account. You can fill this out now, or add it later
              when you&apos;re ready to publish an event.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="gt-form-label">Account name</label>
                <input
                  className="gt-input bg-[var(--gt-bg-surface)]"
                  value={payout.account_name}
                  onChange={(e) => setPayout({ ...payout, account_name: e.target.value })}
                  placeholder="e.g. Bondi Live Pty Ltd"
                  disabled
                  data-testid="payout-account-name"
                />
              </div>
              <div>
                <label className="gt-form-label">Bank name</label>
                <input
                  className="gt-input bg-[var(--gt-bg-surface)]"
                  value={payout.bank_name}
                  onChange={(e) => setPayout({ ...payout, bank_name: e.target.value })}
                  placeholder="e.g. Commonwealth Bank"
                  disabled
                  data-testid="payout-bank-name"
                />
              </div>
              <div>
                <label className="gt-form-label">BSB</label>
                <input
                  className="gt-input bg-[var(--gt-bg-surface)] font-mono"
                  value={payout.bsb}
                  onChange={(e) => setPayout({ ...payout, bsb: e.target.value })}
                  placeholder="062-000"
                  disabled
                  data-testid="payout-bsb"
                />
              </div>
              <div>
                <label className="gt-form-label">Account number</label>
                <input
                  className="gt-input bg-[var(--gt-bg-surface)] font-mono"
                  value={payout.account_number}
                  onChange={(e) => setPayout({ ...payout, account_number: e.target.value })}
                  placeholder="12345678"
                  disabled
                  data-testid="payout-account-number"
                />
              </div>
            </div>
          </div>

          {msg && (
            <div className="flex items-center gap-2 text-sm text-[var(--gt-ink)]" data-testid="profile-success">
              <CheckCircle2 size={16} className="text-[var(--gt-accent)]" /> {msg}
            </div>
          )}
          {error && (
            <div className="text-sm text-[var(--gt-accent)]" data-testid="profile-error">{error}</div>
          )}

          <div className="flex justify-end gap-3">
            <button type="submit" className="gt-btn-primary" disabled={saving} data-testid="profile-save-btn">
              {saving && <Loader2 size={16} className="animate-spin" />}
              Save changes
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
